import { Atom, PrimitiveAtom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import {
  filtersToPersistAtom,
  filterChangesToApplyAtom,
  lastAppliedFiltersAtom,
} from '@/atoms/filters/persistedFilters';
import { resetFiltersToLastAppliedFiltersAtom } from '@/atoms/filters/resetFilters';
import { autoApplyFiltersAtom } from '@/atoms/filters/userFilters';
import { clientOrderByAtom, clientOrderDirectionAtom, orderByAtom, orderDirectionAtom } from '@/atoms/table';
import { useDeepEffect } from '@/hooks/useDeepEffect';
import type { FilterParams } from '@statsbomb/parachute-types';

const useOnApply = (
  aggsFilterParamsAtom: Atom<FilterParams>,
  aggsBaseFiltersAtom: PrimitiveAtom<{} | null>,
  postApplyFunction?: () => void,
) => {
  const setLastAppliedFilters = useSetAtom(lastAppliedFiltersAtom);
  const filterChangesToApply = useAtomValue(filterChangesToApplyAtom);
  const clientOrderBy = useAtomValue(clientOrderByAtom);
  const clientOrderDirection = useAtomValue(clientOrderDirectionAtom);
  const setOrderBy = useSetAtom(orderByAtom);
  const setOrderDirection = useSetAtom(orderDirectionAtom);
  const aggsFilterParams = useAtomValue(aggsFilterParamsAtom);
  const setAggsBaseFilters = useSetAtom(aggsBaseFiltersAtom);

  return () => {
    // keep server side sort atoms up to date when applying new filters
    if (clientOrderBy) {
      setOrderBy(clientOrderBy);
      setOrderDirection(clientOrderDirection);
    }
    setLastAppliedFilters(filterChangesToApply);
    setAggsBaseFilters(aggsFilterParams);
    if (postApplyFunction) postApplyFunction();
  };
};

export const useApplyFiltersButton = (
  aggsFilterParamsAtom: Atom<FilterParams>,
  aggsBaseFiltersAtom: PrimitiveAtom<{} | null>,
  isLoading?: boolean,
  postApplyFunction?: () => void,
) => {
  const onApply = useOnApply(aggsFilterParamsAtom, aggsBaseFiltersAtom, postApplyFunction);
  const filtersToPersist = useAtomValue(filtersToPersistAtom);
  const [lastAppliedFilters, setLastAppliedFilters] = useAtom(lastAppliedFiltersAtom);

  const resetFiltersToLastAppliedFilters = useSetAtom(resetFiltersToLastAppliedFiltersAtom);

  const [autoApplyFilters, setAutoApplyFilters] = useAtom(autoApplyFiltersAtom);

  useDeepEffect(() => {
    /* don't do anything on initial rendering until the default values have been loaded,
      need the lastAppliedFilters === null check here to not keep running the setter
      once the atom is no longer null */
    if (!isLoading && lastAppliedFilters === null) {
      setLastAppliedFilters(filtersToPersist);
    }
  }, [isLoading, lastAppliedFilters]);

  useEffect(() => {
    if (autoApplyFilters) {
      onApply();
      setAutoApplyFilters(false);
    }
  }, [autoApplyFilters]);

  return {
    onCancel: resetFiltersToLastAppliedFilters,
    onApply,
  };
};
